import { useEffect, useState } from 'react';
import axios from 'axios';
import AppURL from '../components/AppUrl';

interface Estimation {
    _id: string;
    type: string;
    fullName: string;
    email: string;
    phone: string;
    brand: string;
    model: string;
    trim: string;
    year: number;
    preferredLanguage: string;
    contactMethod: string;
    description: string;
    images: string[];
    Seen: string[];
    reply: boolean;
    replyBy: string;
    replyMessage: string;
    replyDate: string;
    createdAt: string;
    updatedAt: string;
}

interface UserData {
    name: string;
    email: string;
    id: string;
    role: string;
}

const Estimation = () => {
    const [estimations, setEstimations] = useState<Estimation[]>([]);
    const [selectedEstimation, setSelectedEstimation] = useState<Estimation | null>(null);
    const [zoomedImage, setZoomedImage] = useState<string | null>(null);
    const [zoomLevel, setZoomLevel] = useState<number>(1);
    const [currentUser, setCurrentUser] = useState<UserData | null>(null);
    const [replyMode, setReplyMode] = useState(false);
    const [replyText, setReplyText] = useState('');

    useEffect(() => {
        const userData = localStorage.getItem('userData');
        if (userData) {
            setCurrentUser(JSON.parse(userData));
        }
        fetchEstimations();
    }, []);

    const fetchEstimations = () => {
        axios.get(`${AppURL}/api/estimations/getAll`)
            .then(res => {
                setEstimations(res.data.estimations.reverse());
            })
            .catch(err => console.error('Erreur récupération estimations :', err));
    };

    const markAsSeen = async (estimationId: string) => {
        try {
            await axios.put(`${AppURL}/api/estimations/markAsSeen`, {
                estimationId,
                seenBy: currentUser?.name
            });
            fetchEstimations();
        } catch (err) {
            console.error('Erreur lors du marquage comme vu :', err);
        }
    };

    const handleReplySubmit = async () => {
        if (!selectedEstimation || !currentUser || !replyText.trim()) return;

        try {
            await axios.put(`${AppURL}/api/estimations/reply`, {
                estimationId: selectedEstimation._id,
                replyBy: currentUser.name,
                replyMessage: replyText
            });

            fetchEstimations();
            setReplyMode(false);
            setReplyText('');

            const updatedEstimation = {
                ...selectedEstimation,
                reply: true,
                replyBy: currentUser.name,
                replyMessage: replyText,
                replyDate: new Date().toISOString()
            };
            setSelectedEstimation(updatedEstimation);

        } catch (err) {
            console.error('Erreur lors de l\'envoi de la réponse :', err);
        }
    };

    const handleSelect = async (estimation: Estimation) => {
        if (currentUser && !estimation.Seen.includes(currentUser.name)) {
            await markAsSeen(estimation._id);
        }
        setSelectedEstimation(estimation);
    };

    const handleCloseModal = () => {
        setSelectedEstimation(null);
        setZoomedImage(null);
        setZoomLevel(1);
        setReplyMode(false);
        setReplyText('');
    };

    const handleImageClick = (img: string) => {
        setZoomedImage(img);
        setZoomLevel(1);
    };

    const handleCloseZoom = () => {
        setZoomedImage(null);
    };

    const handleZoom = (direction: 'in' | 'out') => {
        setZoomLevel(prev => {
            const newLevel = direction === 'in' ? prev + 0.2 : prev - 0.2;
            return Math.max(0.5, Math.min(newLevel, 3));
        });
    };

    const getEstimationColor = (est: Estimation) => {
        if (est.reply) return 'bg-green-50 border-green-200';
        if (est.Seen.length > 0) return 'bg-blue-50 border-blue-200';
        return 'bg-yellow-50 border-yellow-200';
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    return (
        <div className="p-4 mt-15">
            <h1 className="text-3xl font-bold mb-4">Estimations</h1>

            <ul className="space-y-2">
                {estimations.map(est => (
                    <li
                        key={est._id}
                        onClick={() => handleSelect(est)}
                        className={`cursor-pointer border p-2 rounded hover:opacity-80 ${getEstimationColor(est)}`}
                    >
                        <div className="flex justify-between items-center">
                            <div>
                                {est.type}  {est.brand} {est.model} ({est.year})
                                <div className="text-xs text-gray-500">
                                    Reçu le: {formatDate(est.createdAt)}
                                </div>
                            </div>
                            <div className="flex space-x-2 text-sm">
                                {est.Seen.length > 0 && (
                                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                        Vu
                                    </span>
                                )}
                                {est.reply && (
                                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
                                        Répondu
                                    </span>
                                )}
                            </div>
                        </div>
                    </li>
                ))}
            </ul>

            {/* Modal pour les détails de l'estimation */}
            {selectedEstimation && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex justify-between items-start mb-4">
                                <h2 className="text-xl font-semibold">Détails de l'estimation</h2>
                                <button
                                    onClick={handleCloseModal}
                                    className="text-gray-500 hover:text-gray-700"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                        <h3 className="font-medium text-gray-700 mb-2">Informations personnelles</h3>
                                        <p><strong>Type:</strong> {selectedEstimation.type}</p>
                                        <p><strong>Nom:</strong> {selectedEstimation.fullName}</p>
                                        <p><strong>Email:</strong> {selectedEstimation.email}</p>
                                        <p><strong>Téléphone:</strong> {selectedEstimation.phone}</p>

                                        <p>
                                            <strong>Langue:</strong> {
                                                selectedEstimation.preferredLanguage === 'fr' ? 'Français' : 'Anglais'
                                            }
                                        </p>

                                        <p>
                                            <strong>Méthode de contact:</strong> {
                                                selectedEstimation.contactMethod === 'both'
                                                    ? 'Email et Téléphone'
                                                    : selectedEstimation.contactMethod === 'email'
                                                        ? 'Email'
                                                        : 'Téléphone'
                                            }
                                        </p>
                                    </div>

                                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                        <h3 className="font-medium text-gray-700 mb-2">Véhicule</h3>
                                        <p><strong>Marque:</strong> {selectedEstimation.brand}</p>
                                        <p><strong>Modèle:</strong> {selectedEstimation.model}</p>
                                        <p><strong>Finition:</strong> {selectedEstimation.trim}</p>
                                        <p><strong>Année:</strong> {selectedEstimation.year}</p>
                                    </div>
                                </div>

                                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                    <h3 className="font-medium text-gray-700 mb-2">Description</h3>
                                    <p className="whitespace-pre-line">{selectedEstimation.description}</p>
                                </div>

                                {selectedEstimation.images.length > 0 && (
                                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                        <h3 className="font-medium text-gray-700 mb-2">Images ({selectedEstimation.images.length})</h3>
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                            {selectedEstimation.images.map((img, index) => (
                                                <img
                                                    key={index}
                                                    src={`${AppURL}/${img}`}
                                                    alt={`estimation-${index}`}
                                                    className="w-full h-40 object-cover rounded cursor-zoom-in"
                                                    onClick={() => handleImageClick(img)}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {selectedEstimation.Seen.length > 0 && (
                                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                        <p><strong>Vu par:</strong> {selectedEstimation.Seen.join(', ')}</p>
                                    </div>
                                )}

                                {selectedEstimation.reply ? (
                                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                                        <h3 className="font-medium text-gray-700 mb-2">Réponse</h3>
                                        <p><strong>Répondu par:</strong> {selectedEstimation.replyBy}</p>
                                        <p><strong>Date de réponse:</strong> {formatDate(selectedEstimation.replyDate)}</p>
                                        <div className="mt-3 p-3 bg-white rounded border border-gray-300">
                                            <p className="text-gray-700 whitespace-pre-line">{selectedEstimation.replyMessage}</p>
                                        </div>
                                    </div>
                                ) : (
                                    currentUser && (
                                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                            {!replyMode ? (
                                                <button
                                                    onClick={() => setReplyMode(true)}
                                                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                                                >
                                                    Répondre
                                                </button>
                                            ) : (
                                                <div>
                                                    <textarea
                                                        value={replyText}
                                                        onChange={(e) => setReplyText(e.target.value)}
                                                        className="w-full p-2 border rounded"
                                                        rows={4}
                                                        placeholder="Écrivez votre réponse ici..."
                                                    />
                                                    <div className="flex space-x-2 mt-2">
                                                        <button
                                                            onClick={handleReplySubmit}
                                                            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                                                            disabled={!replyText.trim()}
                                                        >
                                                            Envoyer
                                                        </button>
                                                        <button
                                                            onClick={() => setReplyMode(false)}
                                                            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                                                        >
                                                            Annuler
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal pour l'image zoomée */}
            {zoomedImage && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
                    <div className="relative max-w-full max-h-full">
                        <img
                            src={`${AppURL}/${zoomedImage}`}
                            alt="zoomed"
                            className="max-w-full max-h-screen"
                            style={{ transform: `scale(${zoomLevel})` }}
                        />

                        <div className="absolute top-4 right-4 flex space-x-2">
                            <button
                                onClick={() => handleZoom('in')}
                                className="bg-white text-black rounded-full w-10 h-10 flex items-center justify-center text-xl disabled:opacity-50"
                                disabled={zoomLevel >= 3}
                            >
                                +
                            </button>
                            <button
                                onClick={() => handleZoom('out')}
                                className="bg-white text-black rounded-full w-10 h-10 flex items-center justify-center text-xl disabled:opacity-50"
                                disabled={zoomLevel <= 0.5}
                            >
                                -
                            </button>
                            <button
                                onClick={handleCloseZoom}
                                className="bg-red-500 text-white rounded-full w-10 h-10 flex items-center justify-center text-xl"
                            >
                                &times;
                            </button>
                        </div>

                        <div className="absolute bottom-4 left-0 right-0 text-center text-white">
                            Niveau de zoom: {(zoomLevel * 100).toFixed(0)}%
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Estimation;